import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO13485_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 13485 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Medical Device Quality Management System — The international
            standard for ensuring quality, safety, and regulatory compliance in
            the medical devices industry.
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 13485 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 13485 is an internationally recognized standard for Quality
              Management Systems specific to the medical devices industry. It is
              designed to ensure that organizations involved in the design,
              development, production, installation, and servicing of medical
              devices consistently meet regulatory, safety, and customer
              requirements.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              ISO 13485 places strong emphasis on risk management, product
              safety, regulatory compliance, and process control, making it a
              critical certification for organizations operating in regulated
              healthcare and medical technology markets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO 13485 */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 13485 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 13485 certification confirms that an organization has
              implemented an effective Medical Device Quality Management System
              (MDQMS) that complies with international regulatory expectations.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The certification focuses on:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Users, text: "Patient safety and effectiveness" },
                { icon: TrendingUp, text: "Regulatory compliance" },
                { icon: ShieldCheck, text: "Risk-based quality management" },
                { icon: Star, text: "Traceability and control" },
                { icon: Target, text: "Manufacturing consistency" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <item.icon
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 13485 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Regulatory Compliance",
                desc: "Aligns quality systems with international medical device regulations and legal requirements.",
              },
              {
                icon: BarChart3,
                title: "Risk Management",
                desc: "Ensures systematic identification, evaluation, and control of risks affecting product safety.",
              },
              {
                icon: Users,
                title: "Product Safety & Quality",
                desc: "Supports consistent production of safe, reliable, and effective medical devices.",
              },
              {
                icon: Award,
                title: "Process Control",
                desc: "Strengthens control over design, production, validation, and change management.",
              },
              {
                icon: Globe,
                title: "Global Market Acceptance",
                desc: "Recognized by regulators, notified bodies, and customers worldwide.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Medical Device QMS */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is a Medical Device Quality Management System (MDQMS)?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              A Medical Device Quality Management System is a structured
              framework that ensures compliance with regulatory and customer
              requirements while maintaining the highest standards of product
              safety and effectiveness.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              A well-implemented MDQMS:
            </p>
            <div className="space-y-3">
              {[
                "Compliance with regulatory and customer requirements",
                "Controlled design and development processes",
                "Validation of critical manufacturing processes",
                "Effective supplier and outsourcing control",
                "Traceability of products, components, and materials",
                "Post-market monitoring, complaints handling, and corrective actions",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mt-0.5 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Importance of ISO 13485 Certification for Businesses
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Ensures compliance with medical device regulations",
                "Reduces risk of product failures and recalls",
                "Enhances patient safety and product reliability",
                "Strengthens customer and regulator confidence",
                "Facilitates access to international healthcare markets",
                "Improves internal process consistency and documentation",
                "Supports long-term business sustainability",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certification Process */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Get ISO 13485 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The certification process typically includes:
            </p>
            <div className="space-y-4">
              {[
                "Conducting a Gap Analysis against ISO 13485 requirements",
                "Designing and implementing a Medical Device QMS",
                "Developing required documentation and records",
                "Implementing risk management throughout the product lifecycle",
                "Training staff on regulatory and quality requirements",
                "Conducting internal audits and management reviews",
                "Certification audit by an accredited certification body",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 13485 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides specialized ISO 13485 consultancy
              services tailored to the medical device and healthcare sector. Our
              experienced consultants ensure smooth MDQMS implementation,
              regulatory alignment, and successful certification.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our support includes:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "MDQMS Design",
                "Risk Management",
                "Documentation",
                "Internal Audits",
                "Regulatory Alignment",
              ].map((sector, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {sector}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              Partnering with MBA Solutions means working with a team
              experienced in medical device quality management and regulatory
              requirements—ensuring a smooth, compliant, and cost-effective
              certification journey.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO13485_Details;
